
import { functions } from '../../firebaseClient';
import type { AttachmentMeta } from "../types/attachments";
import { FEATURES } from "../config/features";

// --- Legacy Interfaces (Keep types to avoid breaking build, but logic is sealed) ---
export interface AIAnalysisRequest {
  fileMeta?: AttachmentMeta;
  files?: AttachmentMeta[];
  purpose?: string;
  mode?: string;
  userInstruction?: string;
  contextType?: string;
  contextId?: string;
  contractId?: string;
  extraContext?: Record<string, any>;
}

/**
 * [SEALED] Legacy Entry Point
 * This function is now permanently disabled via Feature Flag.
 */
export async function analyzeUploadedFile(params: AIAnalysisRequest): Promise<any> {
    if (!FEATURES.AI_LEGACY_ENABLED) {
        console.warn("[AI Service] Legacy AI is sealed.");
        throw new Error("AI 功能已封印 (Legacy Sealed)。請至 Debug 頁面測試 V2。");
    }
    
    // Unreachable code if flag is false
    throw new Error("AI Legacy Endpoint is deprecated.");
}

/**
 * [SEALED] Legacy Assistant Wrapper
 */
export async function callLegalAssistant(payload: any): Promise<{ ok: boolean, text: string, message?: string }> {
    if (!FEATURES.AI_LEGACY_ENABLED) {
        return { ok: false, text: "", message: "AI 功能已封印 (Legacy Sealed)。" };
    }
    return { ok: false, text: "", message: "Deprecated" };
}

// --- AI V2 (New Architecture) ---

export interface AiV2AnalyzeParams {
    file: {
        name: string;
        url: string;     // Must be a valid download URL (Storage)
        contentType?: string;
        size?: number;
    };
    mode?: "extract" | "summary" | "ocr_only";
    ocr?: "auto" | "force" | "off";
    traceId?: string;
}

export interface AiV2Response {
    ok: boolean;
    extractedText: string;
    traceId: string;
    meta?: any;
    error?: {
        code: string;
        message: string;
        details?: any;
    };
    debug?: any;
}

/**
 * AI V2: Call Cloudflare Page Function (Proxy) -> Firebase HTTP
 * No contractId required. Pure file analysis.
 */
export async function analyzeFileV2(params: AiV2AnalyzeParams): Promise<AiV2Response> {
    try {
        const traceId = params.traceId || `client-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
        
        console.log(`[AI V2] Sending request ${traceId}`, params);

        const response = await fetch("/api/ai-v2/analyze", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Trace-Id": traceId
            },
            body: JSON.stringify({
                ...params,
                traceId
            })
        });

        // 確保 Response 是 JSON，即使 HTTP 錯誤
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
            const text = await response.text();
            throw new Error(`Proxy returned non-JSON: ${text.slice(0, 100)}`);
        }

        const json = await response.json();
        return json as AiV2Response;

    } catch (e: any) {
        console.error("[AI V2] Client Error:", e);
        return {
            ok: false,
            traceId: params.traceId || "unknown",
            extractedText: "",
            error: {
                code: "CLIENT_FETCH_ERROR",
                message: e.message || "Network request failed"
            }
        };
    }
}
